import { TranslateService } from '@ngx-translate/core';
import { ActiveToast, ToastrService } from 'ngx-toastr';
import { Injectable } from '@angular/core';
import { SettingService } from '../service/setting.service';

@Injectable({
  providedIn: 'root'
})
export class MessageHandlerService
{
  public patientLabelString: string;

  constructor(
    private toastr: ToastrService,
    private translate: TranslateService,
    private settingService: SettingService)
  {
    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.patientLabelString = sessionSetting.patientLabel;
      }
    });
  }

  /**
   * Display error message based on httpResponse or messageKey/titleKey is provided.
   * httpReponse or messageKey must be provided.
   *
   * @param messageKeyOrHttpResponse httpResponse (object) or messageKey (string)
   * @param titleKey (optional) indicate the translation key for the title to display
   * @param options allow message customization options
   */
  public displayError(messageKeyOrHttpResponse?: any, titleKey?: string, options?: any): void
  {
    if (typeof (messageKeyOrHttpResponse) === 'object')
    {
      // Do not show 404 errors. 404 are redirected to 404 page in ErrorInterceptorService.
      if (messageKeyOrHttpResponse.status === 404)
      {
        return;
      }
      this.translate.get(['OPERATION-FAILED', 'RECORD-LOCKED']).subscribe({
        next: (value) =>
        {
          let msg = '';
          try
          {
            msg = messageKeyOrHttpResponse.error.message;
            // Override the error if optimistic locking error
            if (msg && msg.includes('optimistic locking failed'))
            {
              msg = value['RECORD-LOCKED'];
            }
          }
          catch 
          {
            // Nothing to do
          }
          this.toastr.error(msg, value['OPERATION-FAILED'], options);
        }
      });
    }
    else
    {
      this.showMessage('error', messageKeyOrHttpResponse, titleKey, options);
    }
  }

  /**
   * Display warning message. messageKey must be provided.
   *
   * @param messageKey (required) indicate the translation key for the message to display
   * @param titleKey (optional) indicate the translation key for the title to display
   * @param options (optional) allow message customization options
   */
  public displayWarning(messageKey: string, titleKey?: string, options?: any): void
  {
    this.showMessage('warning', messageKey, titleKey, options);
  }

  /**
   * Display success message. messageKey must be provided.
   *
   * @param messageKey (required) indicate the translation key for the message to display
   * @param titleKey (optional) indicate the translation key for the title to display
   * @param options (optional) allow message customization options
   */
  public displaySuccess(messageKey: string, titleKey?: string, options?: any): void
  {
    this.showMessage('success', messageKey, titleKey, options);
  }

  /**
   * Display info message. messageKey must be provided.
   *
   * @param messageKey (required) indicate the translation key for the message to display
   * @param titleKey (optional) indicate the translation key for the title to display
   * @param options (optional) allow message customization options
   */
  public displayInfo(messageKey: string, titleKey?: string, options?: any): void
  {
    this.showMessage('info', messageKey, titleKey, options);
  }

  /**
   * Display a message without translate lookup.
   */
  public displayDirectMessage(type: string, message: string, title?: string, options?: any): ActiveToast<any>
  {
    return this.displayDirectMessageWithOptions(type, message, title, options);
  }

  /**
   * Display a message without translate lookup.
   */
  private displayDirectMessageWithOptions(type: string, message: string, title?: string, options?: any): ActiveToast<any>
  {
    return this.toastrMessage(type, message, title, options);
  }

  /**
   * Internal message rendering.
   *
   * @param messageType (required) indicate the message type: error, info, warning or info
   * @param messageKey (required) indicate the translation key for the message to display
   * @param titleKey (optional) indicate the translation key for the title to display
   * @param options (optional) allow message customization options
   */
  private showMessage(messageType: string, messageKey?: string, titleKey?: string, options?: any)
  {
    const keys = [];
    if (messageKey) { keys.push(messageKey); }
    if (titleKey) { keys.push(titleKey); }
    this.translate.get(keys).subscribe({
      next: (res) =>
      {
        let index = 0;
        const toastMsg = [];
        toastMsg[0] = '';   // Message
        toastMsg[1] = '';   // Title
        for (const key in res)
        {
          if ({}.hasOwnProperty.call(res, key))
          {
            if (res[key].includes('{{patientLabel}}'))
            {
              toastMsg[index] = res[key].replace('{{patientLabel}}', this.patientLabelString);
            }
            else
            {
              toastMsg[index] = res[key];
            }
            index++;
          }
        }
        // Show
        this.toastrMessage(messageType, toastMsg[0], toastMsg[1], options);
      }
    });
  }

  private toastrMessage(type: string, message: string, title?: string, overrides?: any): ActiveToast<any>
  {
    switch (type)
    {
      case 'error': { return this.toastr.error(message, title, overrides); }
      case 'warning': { return this.toastr.warning(message, title, overrides); }
      case 'success': { return this.toastr.success(message, title, overrides); }
      case 'info': { return this.toastr.info(message, title, overrides); }
    }
  }
}
